import React, { useEffect, useMemo, useState } from "react"
import Popover from "@mui/material/Popover"
import IconsColor from "../../util/iconsColor"
import styles from "./mainTabsTableHeader.module.scss"

// ----- Public types -----

export interface SortOption {
  label: string
  value: string
}

export interface FilterGroup {
  /** Stable key (matches accessor in page-side filter logic), e.g. `"contract_status"`. */
  key: string
  /** Display label in popover + chips, e.g. `"Contract Status"`. */
  label: string
  /** Distinct values for this column, derived by the page from current data. */
  options: { label: string; value: string }[]
}

export interface MainTabsTableHeaderProps {
  // --- Search ---
  searchValue: string
  onSearchChange: (next: string) => void
  searchPlaceholder?: string

  // --- Count text below search ---
  totalCount: number
  filteredCount: number
  /** Singular noun for the items, e.g. `"hotels"` / `"guides"`. Always plural form is fine. */
  itemLabel: string

  // --- Sort ---
  sortValue: string
  onSortChange: (next: string) => void
  sortOptions: SortOption[]

  // --- Filters ---
  filterGroups: FilterGroup[]
  selectedFilters: Record<string, string[]>
  onFiltersChange: (next: Record<string, string[]>) => void

  // --- Add button ---
  addButtonLabel: string
  onAddClick: () => void
  /** Hide the Add button on read-only tabs. Default: true. */
  showAddButton?: boolean
  /** Result count under the search box (`50 of 50 quotations`). Default: true.
   *  Paginated lists hide this because the pager already shows the count. */
  showResultCount?: boolean
  /** Optional actions rendered between Filters and Add (e.g. Export). */
  extraActions?: React.ReactNode
}

// ----- Helpers -----

const countAppliedFilters = (selected: Record<string, string[]>): number =>
  Object.values(selected).reduce((sum, vals) => sum + (vals?.length || 0), 0)

const lookupOptionLabel = (group: FilterGroup, value: string): string =>
  group.options.find((o) => o.value === value)?.label ?? value

// ===== Filter popover (internal) =====

interface FilterPopoverProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  groups: FilterGroup[]
  initialSelected: Record<string, string[]>
  onApply: (next: Record<string, string[]>) => void
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  groups,
  initialSelected,
  onApply,
}) => {
  // Draft state: edits stay local until Apply commits.
  const [draft, setDraft] = useState<Record<string, string[]>>(initialSelected)
  // Track which groups are expanded. Multiple groups can be open at once so users can
  // see/edit several filter categories together. On reopen, auto-expand any group that
  // already has an applied filter (so the user immediately sees what's active), plus the
  // first group as a sensible default when nothing is applied.
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => new Set())

  // Re-seed draft + open-groups whenever the popover (re)opens.
  useEffect(() => {
    if (!open) return
    setDraft(initialSelected)
    const groupsWithApplied = Object.entries(initialSelected)
      .filter(([, vals]) => Array.isArray(vals) && vals.length > 0)
      .map(([k]) => k)
    const next = new Set<string>(groupsWithApplied)
    if (next.size === 0 && groups[0]?.key) next.add(groups[0].key)
    setOpenKeys(next)
  }, [open, initialSelected, groups])

  const toggleValue = (groupKey: string, value: string) => {
    setDraft((prev) => {
      const cur = prev[groupKey] || []
      const has = cur.includes(value)
      const nextVals = has ? cur.filter((v) => v !== value) : [...cur, value]
      const next = { ...prev, [groupKey]: nextVals }
      // Drop empty arrays so countAppliedFilters stays clean.
      if (nextVals.length === 0) delete next[groupKey]
      return next
    })
  }

  const toggleGroup = (groupKey: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      return next
    })
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{ paper: { className: styles.filterPopoverPaper } }}
    >
      <div className={styles.filterPopoverHeader}>
        <span className={styles.filterPopoverTitle}>Filters</span>
        <button
          type="button"
          className={styles.filterPopoverResetBtn}
          onClick={() => setDraft({})}
        >
          Reset
        </button>
      </div>

      <div className={styles.filterPopoverBody}>
        {groups.map((group) => {
          const isOpen = openKeys.has(group.key)
          const selectedSet = new Set(draft[group.key] || [])
          const appliedCount = selectedSet.size
          return (
            <div key={group.key} className={styles.filterGroup}>
              <button
                type="button"
                className={styles.filterGroupHeader}
                onClick={() => toggleGroup(group.key)}
                aria-expanded={isOpen}
              >
                <span className={styles.filterGroupLabelWrap}>
                  <span>{group.label}</span>
                  {appliedCount > 0 && (
                    <span className={styles.filterGroupBadge} aria-label={`${appliedCount} applied`}>
                      {appliedCount}
                    </span>
                  )}
                </span>
                <span className={`${styles.filterGroupCaret} ${isOpen ? styles.filterGroupCaretOpen : ""}`}>
                  <IconsColor iconName="chevronDown" width="14" height="14" fillColor="none" strokeColor="#475569" />
                </span>
              </button>
              {isOpen && (
                <div className={styles.filterGroupOptions}>
                  {group.options.length === 0 && (
                    <div className={styles.filterGroupEmpty}>No options</div>
                  )}
                  {group.options.map((opt) => {
                    const checked = selectedSet.has(opt.value)
                    return (
                      <label
                        key={opt.value}
                        className={`${styles.filterOption} ${checked ? styles.filterOptionChecked : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleValue(group.key, opt.value)}
                        />
                        <span>{opt.label}</span>
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
        {groups.length === 0 && (
          <div className={styles.filterGroupEmpty}>No filters available</div>
        )}
      </div>

      <div className={styles.filterPopoverFooter}>
        <button type="button" className={styles.popoverCancelBtn} onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.popoverApplyBtn}
          onClick={() => {
            onApply(draft)
            onClose()
          }}
        >
          Apply
        </button>
      </div>
    </Popover>
  )
}

// ===== Sort dropdown (internal) =====

interface SortDropdownProps {
  value: string
  onChange: (next: string) => void
  options: SortOption[]
}

const SortDropdown: React.FC<SortDropdownProps> = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [open])

  const current = options.find((o) => o.value === value) || options[0]

  return (
    <div ref={ref} className={styles.sortWrapper}>
      <button
        type="button"
        className={styles.sortTrigger}
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.sortIcon}>↕</span>
        <span className={styles.sortValueText}>{current?.label || "Sort"}</span>
        <span className={`${styles.sortCaret} ${open ? styles.sortCaretOpen : ""}`}>
          <IconsColor iconName="chevronDown" width="14" height="14" fillColor="none" strokeColor="#475569" />
        </span>
      </button>
      {open && (
        <div className={styles.sortMenu} role="listbox">
          {options.map((opt) => {
            const selected = opt.value === value
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={selected}
                className={`${styles.sortMenuItem} ${selected ? styles.sortMenuItemSelected : ""}`}
                onClick={() => {
                  onChange(opt.value)
                  setOpen(false)
                }}
              >
                <span>{opt.label}</span>
                {selected && (
                  <span className={styles.sortMenuCheck}>✓</span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ===== Applied-filter chips row (internal) =====

interface AppliedFilterChipsProps {
  groups: FilterGroup[]
  selected: Record<string, string[]>
  onRemove: (groupKey: string, value: string) => void
  onClearAll: () => void
}

const AppliedFilterChips: React.FC<AppliedFilterChipsProps> = ({ groups, selected, onRemove, onClearAll }) => {
  const flat = useMemo(() => {
    const list: { groupKey: string; groupLabel: string; value: string; valueLabel: string }[] = []
    groups.forEach((g) => {
      ;(selected[g.key] || []).forEach((v) => {
        list.push({
          groupKey: g.key,
          groupLabel: g.label,
          value: v,
          valueLabel: lookupOptionLabel(g, v),
        })
      })
    })
    return list
  }, [groups, selected])

  if (flat.length === 0) return null

  return (
    <div className={styles.chipsRow}>
      {flat.map((chip) => (
        <span key={`${chip.groupKey}-${chip.value}`} className={styles.chip}>
          <span className={styles.chipLabel}>{chip.groupLabel}:</span>
          <span className={styles.chipValue}>{chip.valueLabel}</span>
          <button
            type="button"
            className={styles.chipRemoveBtn}
            onClick={() => onRemove(chip.groupKey, chip.value)}
            aria-label={`Remove ${chip.groupLabel}: ${chip.valueLabel}`}
          >
            <IconsColor iconName="closeIcon" width="10" height="10" fillColor="none" strokeColor="#64748b" />
          </button>
        </span>
      ))}
      <button type="button" className={styles.chipsClearAllBtn} onClick={onClearAll}>
        Clear all
      </button>
    </div>
  )
}

// ===== Main component =====

const MainTabsTableHeader: React.FC<MainTabsTableHeaderProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  totalCount,
  filteredCount,
  itemLabel,
  sortValue,
  onSortChange,
  sortOptions,
  filterGroups,
  selectedFilters,
  onFiltersChange,
  addButtonLabel,
  onAddClick,
  showAddButton = true,
  showResultCount = true,
  extraActions,
}) => {
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null)
  const appliedCount = countAppliedFilters(selectedFilters)

  const handleRemoveChip = (groupKey: string, value: string) => {
    const cur = selectedFilters[groupKey] || []
    const nextVals = cur.filter((v) => v !== value)
    const next = { ...selectedFilters }
    if (nextVals.length === 0) {
      delete next[groupKey]
    } else {
      next[groupKey] = nextVals
    }
    onFiltersChange(next)
  }

  return (
    <div className={styles.headerWrap}>
      <div className={styles.headerRow}>
        {/* Search block */}
        <div className={styles.searchBlock}>
          <div className={styles.searchInputWrap}>
            <span className={styles.searchIcon}>
              <IconsColor iconName="searchIcon" width="16" height="16" fillColor="none" strokeColor="#94a3b8" />
            </span>
            <input
              type="text"
              className={styles.searchInput}
              placeholder={searchPlaceholder || `Search ${itemLabel}`}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              autoComplete="off"
              name="list-search"
            />
            {searchValue?.length > 0 && <span className={styles.crossIcon} onClick={() => onSearchChange("")}>
              <IconsColor iconName="closeIcon" width="16" height="16" fillColor="none" strokeColor="#000" />
            </span>}
          </div>
          {showResultCount && (
            <div className={styles.countText}>
              {filteredCount} of {totalCount} {itemLabel}
            </div>
          )}
        </div>

        {/* Actions block */}
        <div className={styles.actionsBlock}>
          {/* Sort dropdown only renders when the page provides sortOptions. Pages that use
              in-table column-header sorting can pass [] (or omit) to hide it entirely. */}
          {sortOptions.length > 0 && (
            <SortDropdown value={sortValue} onChange={onSortChange} options={sortOptions} />
          )}

          <button
            type="button"
            className={styles.filtersTrigger}
            onClick={(e) => setFilterAnchor(e.currentTarget)}
          >
            <span className={styles.filtersIcon}>
              <IconsColor iconName="filterIcon" width="16" height="16" fillColor="none" strokeColor="#1f3f9a" />
            </span>
            <span>Filters</span>
            {appliedCount > 0 && <span className={styles.filtersBadge}>{appliedCount}</span>}
          </button>

          {extraActions}

          {showAddButton && (
            <button type="button" className={styles.addBtn} onClick={onAddClick}>
              <span className={styles.addBtnPlus}>+</span>
              <span>{addButtonLabel}</span>
            </button>
          )}
        </div>
      </div>

      <AppliedFilterChips
        groups={filterGroups}
        selected={selectedFilters}
        onRemove={handleRemoveChip}
        onClearAll={() => onFiltersChange({})}
      />

      <FilterPopover
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        groups={filterGroups}
        initialSelected={selectedFilters}
        onApply={onFiltersChange}
      />
    </div>
  )
}

export default MainTabsTableHeader
